import { useEffect, useMemo, useState } from 'react';

import type { SkillAccessRow } from '../../lib/api-client';

interface Props {
  rows: SkillAccessRow[];
  /** Async load state of the reconciled view. */
  status: 'idle' | 'loading' | 'success' | 'error';
  saveStatus: 'idle' | 'saving' | 'success' | 'error';
  error?: string | undefined;
  /** Replace-set the grant list. */
  onSave: (skillIds: string[]) => Promise<void>;
  /**
   * When false, the subject runs with all skills regardless of grants (e.g. a
   * creator_mode role). We render `disabledNote` instead of an editor.
   */
  editable?: boolean;
  /** Note shown when editable is false. */
  disabledNote?: string;
  /** Disable interaction (e.g. while a parent mutation is in flight). */
  disabled?: boolean;
}

/**
 * Skill-access editor (used for ROLES). Renders a checkbox list of the live
 * skills with granted state, plus any stale grants (skills no longer in the
 * repo) flagged with a one-click prune. Local pending selection; Save
 * replace-sets via onSave.
 */
export function SkillAccessEditor({
  rows,
  status,
  saveStatus,
  error,
  onSave,
  editable = true,
  disabledNote,
  disabled = false,
}: Props) {
  // Pending selection of *live* (non-stale) granted skill ids.
  const liveRows = useMemo(() => rows.filter((r) => !r.stale), [rows]);
  const staleRows = useMemo(() => rows.filter((r) => r.stale), [rows]);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Re-seed selection whenever the server view changes (load or after save).
  useEffect(() => {
    setSelected(new Set(rows.filter((r) => r.granted && !r.stale).map((r) => r.skill_id)));
  }, [rows]);

  const baseline = useMemo(
    () => new Set(rows.filter((r) => r.granted && !r.stale).map((r) => r.skill_id)),
    [rows],
  );
  const dirty = useMemo(() => {
    if (selected.size !== baseline.size) return true;
    for (const id of selected) if (!baseline.has(id)) return true;
    return false;
  }, [selected, baseline]);

  const busy = disabled || saveStatus === 'saving';

  const toggle = (id: string) => {
    if (busy) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = () => {
    if (busy || !dirty) return;
    void onSave(Array.from(selected));
  };

  const pruneStale = () => {
    // Stale ids are simply omitted from the next replace-set; save the current
    // (live) selection to drop them.
    if (busy) return;
    void onSave(Array.from(selected));
  };

  if (!editable) {
    return (
      <div style={noteStyle} data-testid="skill-access-creator-note">
        {disabledNote ??
          'This role has Creator Mode and access to all skills. Per-skill access applies only to standard execution roles.'}
      </div>
    );
  }

  if (status === 'loading') {
    return <div style={mutedStyle} data-testid="skill-access-loading">Loading skills…</div>;
  }
  if (status === 'error') {
    return (
      <div style={errorStyle} role="alert" data-testid="skill-access-error">
        {error ?? 'Failed to load skill access.'}
      </div>
    );
  }
  if (liveRows.length === 0 && staleRows.length === 0) {
    return <div style={mutedStyle} data-testid="skill-access-empty">No skills available in this environment.</div>;
  }

  return (
    <div data-testid="skill-access-editor">
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxHeight: 220,
          overflowY: 'auto',
          border: '1px solid var(--border-strong)',
          borderRadius: 6,
          padding: 4,
        }}
      >
        {liveRows.map((r) => {
          const checked = selected.has(r.skill_id);
          return (
            <label
              key={r.skill_id}
              title={r.description || r.name}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                padding: '6px 8px',
                borderRadius: 4,
                cursor: busy ? 'not-allowed' : 'pointer',
                background: checked ? 'var(--accent-soft)' : 'transparent',
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={busy}
                onChange={() => toggle(r.skill_id)}
                style={{ marginTop: 2, accentColor: 'var(--accent)' }}
              />
              <span style={{ minWidth: 0 }}>
                <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: checked ? 600 : 400 }}>
                  {r.name}
                </span>
                {r.description && (
                  <span
                    style={{
                      display: 'block',
                      fontSize: 11,
                      color: 'var(--text-soft)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 360,
                    }}
                  >
                    {r.description}
                  </span>
                )}
              </span>
            </label>
          );
        })}
      </div>

      {staleRows.length > 0 && (
        <div
          style={{
            marginTop: 8,
            padding: '8px 10px',
            background: 'rgba(192,57,43,0.06)',
            border: '1px solid rgba(192,57,43,0.18)',
            borderRadius: 6,
            fontSize: 11,
            color: 'var(--text)',
          }}
          data-testid="skill-access-stale"
        >
          <div style={{ color: 'var(--kind-failure)', fontWeight: 600, marginBottom: 4 }}>
            {staleRows.length} grant{staleRows.length === 1 ? '' : 's'} reference a skill no longer in
            the repo:
          </div>
          <div style={{ color: 'var(--text-soft)' }}>
            {staleRows.map((r) => r.skill_id).join(', ')}
          </div>
          <button type="button" onClick={pruneStale} disabled={busy} style={pruneButtonStyle}>
            {busy ? 'Pruning…' : 'Prune stale grants'}
          </button>
        </div>
      )}

      {error && saveStatus === 'error' && (
        <div style={errorStyle} role="alert">
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          data-testid="skill-access-save"
          style={{
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 500,
            background: 'var(--accent)',
            color: '#fff',
            border: 0,
            borderRadius: 6,
            cursor: busy || !dirty ? 'not-allowed' : 'pointer',
            opacity: busy || !dirty ? 0.6 : 1,
            fontFamily: 'inherit',
          }}
        >
          {saveStatus === 'saving' ? 'Saving…' : 'Save skill access'}
        </button>
      </div>
    </div>
  );
}

const mutedStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--text-soft)',
  padding: '8px 0',
};

const noteStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--text-soft)',
  lineHeight: 1.5,
  padding: '8px 10px',
  background: 'var(--bg)',
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
};

const errorStyle: React.CSSProperties = {
  marginTop: 8,
  padding: '6px 10px',
  background: 'rgba(192,57,43,0.06)',
  border: '1px solid rgba(192,57,43,0.18)',
  borderRadius: 4,
  fontSize: 11,
  color: 'var(--kind-failure)',
};

const pruneButtonStyle: React.CSSProperties = {
  marginTop: 6,
  padding: '5px 10px',
  fontSize: 11,
  fontWeight: 500,
  background: 'transparent',
  color: 'var(--kind-failure)',
  border: '1px solid rgba(192,57,43,0.3)',
  borderRadius: 6,
  cursor: 'pointer',
  fontFamily: 'inherit',
};
